import { useMemo, useState, type DragEvent } from "react";
import { ChevronLeft, ChevronRight, MapPin } from "lucide-react";
import { useTenant } from "@/lib/tenant-context.js";
import { useAsync } from "@/hooks/use-async.js";
import { useKeyboardShortcuts } from "@/hooks/use-keyboard-shortcuts.js";
import { ApiError } from "@/lib/api.js";
import { Button } from "@/components/ui/button.js";
import { Skeleton } from "@/components/ui/skeleton.js";
import { Badge } from "@/components/ui/badge.js";
import { useToast } from "@/components/ui/toast.js";
import { addDays, colorForKey, colorForKeyBorder, formatDate, formatTime } from "@/lib/format.js";
import type { Appointment, Customer, DispatchStop, Resource, Service, SyncConflict } from "@/lib/types.js";
import { BannerStack } from "@/views/banner-stack.js";
import { DispatchMap } from "@/views/dispatch-map.js";
import { AppointmentDialog } from "@/views/appointment-dialog.js";
import { cn } from "@/lib/cn.js";

const DAY_START_HOUR = 7;
const BUFFER_MINUTES_AFTER = 5;

/**
 * Dispatch view per REQ-033 (ServiceTitan pattern). Tech columns = resources of
 * type provider/vehicle. Each column shows the day's stops in order with ETAs
 * and a real OpenStreetMap polyline. Drag a stop card to a different tech column
 * AND a target slot (above first, between stops, or after last) to invoke
 * POST /appointments/{id}/reschedule with the inferred new starts_at (REQ-034).
 */
export function DispatchPage(): React.JSX.Element {
  const { api, profile } = useTenant();
  const { toast } = useToast();
  const [day, setDay] = useState(() => new Date());
  const [selected, setSelected] = useState<Appointment | null>(null);
  const dateStr = day.toISOString().slice(0, 10);

  const resources = useAsync(() => api.listResources(), [api]);
  const services = useAsync(() => api.listServices(), [api]);
  const customers = useAsync(() => api.listCustomers(), [api]);
  const conflicts = useAsync(() => api.listSyncConflicts(), [api]);
  const dispatchByResource = useAsync(async () => {
    const list = (await api.listResources()).data;
    const result = new Map<string, DispatchStop[]>();
    for (const resource of list) {
      try {
        const route = await api.dispatchRoute({ resource_id: resource.id, date: dateStr });
        result.set(resource.id, route.data ?? []);
      } catch {
        result.set(resource.id, []);
      }
    }
    return result;
  }, [api, dateStr]);

  function reload(): void {
    dispatchByResource.reload();
    conflicts.reload();
  }

  async function reschedule(
    appointmentId: string,
    targetResourceId: string,
    newStartsAt: string
  ): Promise<void> {
    try {
      await api.rescheduleAppointment(appointmentId, {
        starts_at: newStartsAt,
        primary_resource_id: targetResourceId
      });
      toast({ title: "Rescheduled", variant: "success" });
      reload();
    } catch (error) {
      const code = error instanceof ApiError ? error.code : "error";
      const msg = error instanceof Error ? error.message : String(error);
      toast({ title: code, description: msg, variant: "destructive" });
    }
  }

  const visibleResources = useMemo(
    () =>
      (resources.data?.data ?? []).filter(
        (r) => !r.deleted_at && (profile?.default_view !== "dispatch" || r.role !== "room")
      ),
    [resources.data, profile]
  );

  useKeyboardShortcuts({
    j: () => setDay((d) => new Date(addDays(d.toISOString(), 1))),
    k: () => setDay((d) => new Date(addDays(d.toISOString(), -1)))
  });

  if (resources.loading || dispatchByResource.loading) {
    return <Skeleton className="h-[600px] w-full" />;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={() => setDay((d) => new Date(addDays(d.toISOString(), -1)))}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="sm" onClick={() => setDay(new Date())}>
            Today
          </Button>
          <Button variant="ghost" size="icon" onClick={() => setDay((d) => new Date(addDays(d.toISOString(), 1)))}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          <span className="ml-2 text-sm font-medium">{formatDate(day.toISOString())}</span>
        </div>
        <Badge variant="outline">
          <MapPin className="mr-1 h-3 w-3" />
          Trades dispatch
        </Badge>
      </div>
      <div className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0">
        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: `repeat(${Math.max(1, visibleResources.length)}, minmax(280px, 1fr))`
          }}
        >
          {visibleResources.map((resource) => (
          <TechColumn
            key={resource.id}
            day={day}
            resource={resource}
            stops={dispatchByResource.data?.get(resource.id) ?? []}
            services={services.data?.data ?? []}
            customers={customers.data?.data ?? []}
            conflicts={conflicts.data?.data ?? []}
            onReassign={(appointmentId, newStartsAt) => reschedule(appointmentId, resource.id, newStartsAt)}
            onSelect={(a) => setSelected(a)}
          />
          ))}
        </div>
      </div>
      <AppointmentDialog
        open={selected !== null}
        onOpenChange={(v) => !v && setSelected(null)}
        appointment={selected}
        customers={customers.data?.data ?? []}
        services={services.data?.data ?? []}
        resources={resources.data?.data ?? []}
        conflicts={conflicts.data?.data ?? []}
        onChanged={reload}
      />
    </div>
  );
}

export interface DropSlot {
  /** ISO target start time for an appointment dropped here. */
  startsAt: string;
  /** Human-readable hint shown during drag. */
  label: string;
}

export function computeDropSlots(day: Date, stops: DispatchStop[]): DropSlot[] {
  // Slot 0 = before first stop, Slot k = after stop[k-1]. Always at least one slot
  // (insertion point at the bottom of an empty column).
  const dayStart = new Date(day);
  dayStart.setHours(DAY_START_HOUR, 0, 0, 0);
  const slots: DropSlot[] = [];

  if (stops.length === 0) {
    slots.push({ startsAt: dayStart.toISOString(), label: `${formatTime(dayStart.toISOString())} (start of day)` });
    return slots;
  }

  // Above the first stop: schedule at day start (or earlier than the first stop —
  // but day start is the safe floor; the user can refine via PATCH).
  slots.push({
    startsAt: dayStart.toISOString(),
    label: `${formatTime(dayStart.toISOString())} (start of day)`
  });

  // Between each pair: schedule at previous.ends_at + buffer.
  for (let i = 0; i < stops.length - 1; i++) {
    const prev = stops[i]!;
    const at = new Date(prev.appointment.ends_at);
    at.setMinutes(at.getMinutes() + BUFFER_MINUTES_AFTER);
    slots.push({ startsAt: at.toISOString(), label: `${formatTime(at.toISOString())} (after ${i + 1})` });
  }

  // After the last stop: end + buffer.
  const last = stops[stops.length - 1]!;
  const after = new Date(last.appointment.ends_at);
  after.setMinutes(after.getMinutes() + BUFFER_MINUTES_AFTER);
  slots.push({ startsAt: after.toISOString(), label: `${formatTime(after.toISOString())} (end of route)` });

  return slots;
}

function TechColumn({
  day,
  resource,
  stops,
  services,
  customers,
  conflicts,
  onReassign,
  onSelect
}: {
  day: Date;
  resource: Resource;
  stops: DispatchStop[];
  services: Service[];
  customers: Customer[];
  conflicts: SyncConflict[];
  onReassign: (appointmentId: string, newStartsAt: string) => void;
  onSelect: (a: Appointment) => void;
}): React.JSX.Element {
  const serviceById = new Map(services.map((s) => [s.id, s]));
  const customerById = new Map(customers.map((c) => [c.id, c]));
  const slots = computeDropSlots(day, stops);

  return (
    <div className="flex flex-col gap-2 rounded-md border bg-card p-2">
      <div className="flex items-center justify-between">
        <div className="font-medium text-sm">{resource.name}</div>
        <Badge variant="outline">{stops.length} stops</Badge>
      </div>
      <DispatchMap resource={resource} stops={stops} customers={customerById} />
      <ul className="flex flex-col">
        {stops.length === 0 && (
          <DropZone slot={slots[0]!} onDropAppointment={onReassign}>
            <li className="rounded border border-dashed p-2 text-center text-xs text-muted-foreground">
              No assignments. Drop a job here.
            </li>
          </DropZone>
        )}
        {stops.length > 0 && (
          <>
            {/* Drop above first stop */}
            <ThinDropZone slot={slots[0]!} onDropAppointment={onReassign} />
            {stops.map((stop, i) => (
              <div key={stop.appointment.id}>
                <StopCard
                  stop={stop}
                  service={serviceById.get(stop.appointment.service_id)}
                  customer={customerById.get(stop.appointment.customer_id)}
                  resourceName={stop.appointment.resource_name ?? resource.name}
                  conflicts={conflicts}
                  onSelect={() => onSelect(stop.appointment)}
                />
                {/* Drop slot below this stop */}
                <ThinDropZone slot={slots[i + 1]!} onDropAppointment={onReassign} />
              </div>
            ))}
          </>
        )}
      </ul>
    </div>
  );
}

function StopCard({
  stop,
  service,
  customer,
  resourceName,
  conflicts,
  onSelect
}: {
  stop: DispatchStop;
  service?: Service;
  customer?: Customer;
  /**
   * PRD principal-scoped-reads-and-crm-refs (REQ-PSR-008): caller passes
   * `appointment.resource_name ?? resource.name` so legacy rows keep
   * rendering after a CRM-side rename.
   */
  resourceName?: string;
  conflicts: SyncConflict[];
  onSelect: () => void;
}): React.JSX.Element {
  const customerName = stop.appointment.customer_name ?? customer?.name ?? "—";
  return (
    <li
      draggable
      onDragStart={(event) => {
        event.dataTransfer.setData("application/x-appointment-id", stop.appointment.id);
        event.dataTransfer.effectAllowed = "move";
      }}
      onClick={onSelect}
      className="flex flex-col gap-1 rounded-md border p-2 text-xs"
      style={{
        background: colorForKey(stop.appointment.service_id),
        borderColor: colorForKeyBorder(stop.appointment.service_id)
      }}
    >
      <div className="flex items-center justify-between">
        <span className="font-medium">{service?.name ?? "Stop"}</span>
        <span className="opacity-70">+{stop.eta_minutes}m ETA</span>
      </div>
      <div className="flex items-center justify-between text-[11px] opacity-80">
        <span>{customerName}</span>
        <span>{formatTime(stop.appointment.starts_at)}</span>
      </div>
      {resourceName && (
        <div className="text-[11px] opacity-70">
          <span data-testid={`dispatch-stop-resource-${stop.appointment.id}`}>{resourceName}</span>
        </div>
      )}
      <BannerStack appointment={stop.appointment} conflicts={conflicts} />
    </li>
  );
}

/** Thin drop zone that shows a target line + time during drag. */
function ThinDropZone({
  slot,
  onDropAppointment
}: {
  slot: DropSlot;
  onDropAppointment: (appointmentId: string, newStartsAt: string) => void;
}): React.JSX.Element {
  const [active, setActive] = useState(false);
  return (
    <div
      onDragOver={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setActive(true);
      }}
      onDragLeave={() => setActive(false)}
      onDrop={(e: DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        setActive(false);
        const id = e.dataTransfer.getData("application/x-appointment-id");
        if (id) onDropAppointment(id, slot.startsAt);
      }}
      className={cn(
        "h-2 w-full transition-colors",
        active && "h-6 rounded border-2 border-dashed border-primary bg-primary/10"
      )}
    >
      {active && (
        <div className="text-center text-[10px] text-primary">{slot.label}</div>
      )}
    </div>
  );
}

/** Empty-column drop zone — full-card area accepting a drop. */
function DropZone({
  slot,
  onDropAppointment,
  children
}: {
  slot: DropSlot;
  onDropAppointment: (appointmentId: string, newStartsAt: string) => void;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <div
      onDragOver={(e) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
      }}
      onDrop={(e) => {
        e.preventDefault();
        const id = e.dataTransfer.getData("application/x-appointment-id");
        if (id) onDropAppointment(id, slot.startsAt);
      }}
    >
      {children}
    </div>
  );
}
